export const MODE_COMPACT = 'compact'
export const MODE_FULL = 'full'

// Порог авто-режима: окно шире 640px — полный вид, уже — компактный.
export const COMPACT_THRESHOLD = 640

export const MIN_WIDTH = 380
export const MIN_HEIGHT = 260

export const DEFAULT_WIDTH = 720
export const DEFAULT_HEIGHT = 480

export function autoMode(width) {
  return width < COMPACT_THRESHOLD ? MODE_COMPACT : MODE_FULL
}

export function isModeOverride(value) {
  return value === MODE_COMPACT || value === MODE_FULL
}

// Ручной оверрайд сбрасывается при пересечении порога авто-режима.
export function normalizeOverrideOnResize(prevWidth, nextWidth, override) {
  if (!isModeOverride(override)) return null
  if (autoMode(prevWidth) === autoMode(nextWidth)) return override
  return null
}

export function resolveMode(width, override) {
  return isModeOverride(override) ? override : autoMode(width)
}

export function clampSize(width, height, viewport = null) {
  let w = Math.max(MIN_WIDTH, Math.round(width))
  let h = Math.max(MIN_HEIGHT, Math.round(height))
  if (viewport) {
    w = Math.min(w, Math.max(MIN_WIDTH, Math.round(viewport.width)))
    h = Math.min(h, Math.max(MIN_HEIGHT, Math.round(viewport.height)))
  }
  return { width: w, height: h }
}

// Окно всегда целиком внутри вьюпорта.
export function clampPosition(x, y, width, height, viewport) {
  const maxX = Math.max(0, Math.round(viewport.width - width))
  const maxY = Math.max(0, Math.round(viewport.height - height))
  return {
    x: Math.min(Math.max(0, Math.round(x)), maxX),
    y: Math.min(Math.max(0, Math.round(y)), maxY),
  }
}

export function defaultPosition(viewport) {
  // Чуть правее и ниже центра, чтобы не перекрывать шапку таблиц.
  const x = Math.max(16, Math.round((viewport.width - DEFAULT_WIDTH) / 2) + 40)
  const y = Math.max(16, Math.round((viewport.height - DEFAULT_HEIGHT) / 2) - 20)
  return clampPosition(x, y, DEFAULT_WIDTH, DEFAULT_HEIGHT, viewport)
}

export function parseTrip360Hash(hash) {
  if (typeof hash !== 'string' || !hash) return null
  const h = hash.startsWith('#') ? hash.slice(1) : hash
  const match = /^trip360=([^&]+)/.exec(h)
  if (!match) return null
  const key = decodeURIComponent(match[1]).trim()
  return key || null
}

export function stripTrip360Hash(hash) {
  if (typeof hash !== 'string' || !hash) return ''
  const h = hash.startsWith('#') ? hash.slice(1) : hash
  if (h.startsWith('trip360=')) return ''
  return hash
}

export function normalizeSearchQuery(q) {
  return String(q ?? '').trim().slice(0, 100)
}

// Ключ для trip360/trip: приоритет — номер карточки, затем ERP trip_id, затем uid.
export function searchRowKey(row) {
  if (!row) return null
  const key = row.distr_id || row.erp_trip_id || row.uid
  return key == null || key === '' ? null : String(key)
}


// Standalone-страница 360-обзора: /trip360/<ключ> — открывается в новом окне/вкладке.
export const TRIP360_PAGE_PATH = 'trip360'

// Абсолютный URL standalone-страницы 360 для ключа (distr_id | erp_trip_id | uid).
export function trip360PageUrl(key, { origin = '', base = '/' } = {}) {
  const normalized = key == null ? '' : String(key).trim()
  if (!normalized) return null
  const cleanBase = base.endsWith('/') ? base : `${base}/`
  return `${origin}${cleanBase}${TRIP360_PAGE_PATH}/${encodeURIComponent(normalized)}`
}

// Строка features для window.open(): размер клампится, позиция опциональна.
export function trip360WindowFeatures({ width, height, x, y } = {}) {
  const size = clampSize(
    Number.isFinite(Number(width)) ? width : DEFAULT_WIDTH,
    Number.isFinite(Number(height)) ? height : DEFAULT_HEIGHT,
  )
  const parts = [`width=${size.width}`, `height=${size.height}`]
  if (Number.isFinite(Number(x))) parts.push(`left=${Math.max(0, Math.round(x))}`)
  if (Number.isFinite(Number(y))) parts.push(`top=${Math.max(0, Math.round(y))}`)
  // popup-хром: без меню/тулбара; адресную строку браузеры прячут частично (только домен).
  parts.push('popup=true', 'menubar=no', 'toolbar=no', 'location=no')
  return parts.join(',')
}

// Позиция нового окна с центровкой поверх вьюпорта родительского окна.
export function centeredOverViewport({
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  viewportX = 0,
  viewportY = 0,
  viewportWidth = 0,
  viewportHeight = 0,
} = {}) {
  const w = Math.max(MIN_WIDTH, Math.round(Number(width) || 0) || DEFAULT_WIDTH)
  const h = Math.max(MIN_HEIGHT, Math.round(Number(height) || 0) || DEFAULT_HEIGHT)
  const x = viewportX + (viewportWidth - w) / 2
  const y = viewportY + (viewportHeight - h) / 2
  return { x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) }
}

// Цветовые классы статус-пилюли (общие для попапа, страницы и контента).
export const TRIP360_STATUS_STYLES = {
  'Открыт': 't360-pill-open',
  'Закрыт': 't360-pill-closed',
  'Отменен': 't360-pill-gray',
  'Удален': 't360-pill-gray',
  'Устарел': 't360-pill-gray',
  'Срыв': 't360-pill-red',
  'Отказ': 't360-pill-orange',
}

export function trip360StatusClass(status) {
  return TRIP360_STATUS_STYLES[status] || 't360-pill-gray'
}

// Геометрия окна (позиция/размер/свёрнутость) переживает перезагрузку страницы.
export const GEOMETRY_STORAGE_KEY = 'trip360:geometry'

export function loadTrip360Geometry(viewport) {
  try {
    const raw = window.localStorage.getItem(GEOMETRY_STORAGE_KEY)
    if (!raw) return null
    const saved = JSON.parse(raw)
    const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
    const savedWidth = num(saved.width)
    const savedHeight = num(saved.height)
    const savedX = num(saved.x)
    const savedY = num(saved.y)
    if (savedWidth === null || savedHeight === null || savedX === null || savedY === null) {
      return null
    }
    // Вьюпорт мог стать меньше (другой монитор) — вписываем сохранённое окно в экран.
    const size = clampSize(savedWidth, savedHeight, viewport)
    const pos = clampPosition(savedX, savedY, size.width, size.height, viewport)
    return { ...pos, ...size, collapsed: saved.collapsed === true }
  } catch {
    return null
  }
}

export function saveTrip360Geometry(patch) {
  try {
    const raw = window.localStorage.getItem(GEOMETRY_STORAGE_KEY)
    const current = raw ? JSON.parse(raw) : {}
    window.localStorage.setItem(GEOMETRY_STORAGE_KEY, JSON.stringify({ ...current, ...patch }))
  } catch {
    // Приватный режим / переполненная квота — геометрия не критична.
  }
}

// ── Хронология работы логиста (distribution_status_history) ──

export const HISTORY_SOURCE_LABELS = {
  manual: 'вручную',
  system: 'система',
  email: 'email',
  sync: 'синк',
  timeout: 'таймаут',
  api: 'api',
}

const HISTORY_QUEUE_STATE_LABELS = {
  pending: 'ожидание',
  worker: 'в работе',
  pending_queue: 'очередь ожидания',
  internal_booking: 'внутреннее бронирование',
}

const HISTORY_QUEUE_ACTION_LABELS = {
  join: 'взял',
  leave: 'освободил',
  prolong: 'продлил',
}

// Метка статуса карточки (коды StatusEnum distribution_data).
const HISTORY_STATUS_NAMES = {
  1: 'Открыт',
  2: 'Закрыт',
  4: 'Отменен',
  8: 'Удален',
  16: 'Устарел',
  32: 'Срыв',
  64: 'Отказ',
}

function historyTime(occurredAt) {
  const ts = Date.parse(occurredAt || '')
  if (Number.isNaN(ts)) return String(occurredAt || '—')
  const d = new Date(ts)
  const p = (n) => String(n).padStart(2, '0')
  return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`
}

function historyWho(ev) {
  const name = ev.actor || ev.interactor || null
  return name || null
}

// Чистая функция: событие бекенда → { time, source, sourceLabel, text }.
export function formatHistoryEvent(ev) {
  const e = ev || {}
  const time = historyTime(e.occurred_at)
  const source = e.source || 'system'
  const sourceLabel = HISTORY_SOURCE_LABELS[source] || source
  const who = historyWho(e)
  const reason = e.reason ? ` — ${e.reason}` : ''

  if (e.event_type === 'queue_event') {
    const state = HISTORY_QUEUE_STATE_LABELS[e.queue_state] || e.queue_state || ''
    const action = HISTORY_QUEUE_ACTION_LABELS[e.queue_action] || e.queue_action || ''
    return { time, source, sourceLabel, text: `${state}: ${action}${who ? ` (${who})` : ''}${reason}` }
  }

  const from = e.status_from_name
    || (e.status_from != null ? HISTORY_STATUS_NAMES[e.status_from] || e.status_from : null)
  const to = e.status_to_name
    || (e.status_to != null ? HISTORY_STATUS_NAMES[e.status_to] || e.status_to : '—')
  return {
    time,
    source,
    sourceLabel,
    text: `${from ? `${from} → ` : ''}${to}${who ? ` (${who})` : ''}${reason}`,
  }
}
